import {makeAutoObservable} from "mobx";
import axios from "axios";
import {getPageStore} from "./PageStore";
import {getStore, useStore} from "./index";
import {getModalStore} from "./ModalStore";

/**
 * @typedef {object} Folder
 * @property {string} _id
 * @property {string} name
 * @property {string} path
 * @property {string?} type
 * @property {number?} depth
 * @property {File[]?} files
 * @property {Folder[]?} children
 * @property {string?} createdAt
 * @property {string?} updatedAt
 * @property {string?} parent
 */

/**
 * @typedef {object} File
 * @property {string} _id
 * @property {string} parent
 * @property {string} type
 * @property {string?} board
 * @property {string?} page
 * @property {string} createdAt
 * @property {string} updatedAt
 */
/**
 * @typedef {object} Bookmark
 * @property {string} _id
 * @property {string} user
 * @property {string} type
 * @property {string} name
 * @property {Folder} folder
 * @property {string} createdAt
 * @property {string} updatedAt
 */

export class FolderStore {
    /** @type {Record<string,Folder>} */
    folders = {
        PAGE: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        CLIP: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        COMMON: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        TEMPLATE: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        TRASHCAN_PAGE: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        TRASHCAN_CLIP: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        TRASHCAN_COMMON: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        TRASHCAN_TEMPLATE: {
            _id: "",
            name: "",
            path: "",
            depth: 1,
            files: [],
            children: [],
            createdAt: "",
            updatedAt: ""
        }
    };
    /** @type {Record<string,Record<string,Folder>>} */
    folderMapById = {
        PAGE: {},
        CLIP: {},
        COMMON: {},
        TEMPLATE: {},
        TRASHCAN_PAGE: {},
        TRASHCAN_CLIP: {},
        TRASHCAN_COMMON: {},
        TRASHCAN_TEMPLATE: {},
    };
    /** @type {Record<string,Folder>} */
    findFolders = {
        PAGE: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        CLIP: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        COMMON: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""},
        TEMPLATE: {_id: "", name: "", path: "", depth: 1, files: [], children: [], createdAt: "", updatedAt: ""}
    };
    /** @type {Record<string,Bookmark[]>} */
    bookmarks = {PAGE: [], CLIP: [], COMMON: [], TEMPLATE: []};
    /** @type {Record<string,Bookmark[]>} */
    findBookmarks = {PAGE: [], CLIP: [], COMMON: [], TEMPLATE: []};
    /** @type {Record<string,string>} */
    sortValue = {
        PAGE: "name",
        CLIP: "createdAt",
        COMMON: "createdAt",
        TEMPLATE: "createdAt",
        TRASHCAN_PAGE: "-expiresAt",
        TRASHCAN_TEMPLATE: "-expiresAt",
        TRASHCAN_CLIP: "-expiresAt",
        TRASHCAN_COMMON: "-expiresAt"
    };
    /** @type {Record<string,string>} */
    sortBookmarkValue = {PAGE: "name", CLIP: "createdAt", COMMON: "createdAt", TEMPLATE: "createdAt"};
    /** @type {Record<string,string[]>} */
    openFolders = {
        PAGE: [],
        CLIP: [],
        COMMON: [],
        TEMPLATE: [],
        TRASHCAN_PAGE: [],
        TRASHCAN_CLIP: [],
        TRASHCAN_COMMON: [],
        TRASHCAN_TEMPLATE: [],
    };
    /** @type {Record<string,Folder>} */
    currentFolder = {
        PAGE: undefined,
        CLIP: undefined,
        COMMON: undefined,
        TEMPLATE: undefined,
        TRASHCAN_PAGE: undefined,
        TRASHCAN_CLIP: undefined,
        TRASHCAN_COMMON: undefined,
        TRASHCAN_TEMPLATE: undefined
    };
    /** @type {Array<{openFolders:Record<string,string[]>,currentFolder:Record<string,Folder>}>} */
    modalFolderInfo = [
        {
            openFolders: {
                PAGE: [],
                CLIP: [],
                COMMON: [],
                TEMPLATE: [],
                TRASHCAN_PAGE: [],
                TRASHCAN_CLIP: [],
                TRASHCAN_COMMON: [],
                TRASHCAN_TEMPLATE: []
            },
            currentFolder: {
                PAGE: undefined,
                CLIP: undefined,
                COMMON: undefined,
                TEMPLATE: undefined,
                TRASHCAN_PAGE: undefined,
                TRASHCAN_CLIP: undefined,
                TRASHCAN_COMMON: undefined,
                TRASHCAN_TEMPLATE: undefined
            }
        },
        {
            openFolders: {
                PAGE: [],
                CLIP: [],
                COMMON: [],
                TEMPLATE: [],
                TRASHCAN_PAGE: [],
                TRASHCAN_CLIP: [],
                TRASHCAN_COMMON: [],
                TRASHCAN_TEMPLATE: []
            },
            currentFolder: {
                PAGE: undefined,
                CLIP: undefined,
                COMMON: undefined,
                TEMPLATE: undefined,
                TRASHCAN_PAGE: undefined,
                TRASHCAN_CLIP: undefined,
                TRASHCAN_COMMON: undefined,
                TRASHCAN_TEMPLATE: undefined
            }
        },
    ];

    /** @type {Record<string,string>} */
    editorBoardList = {name: "템플릿", link: "temp", path: "TEMPLATE"};

    /** @type {{TREE:boolean, ETC:boolean}} */
    addFolder = {TREE: false, ETC: false};
    /** @type {Record<string,{id:string,name:string}>} */
    addBookmark = {
        PAGE: undefined, TEMPLATE: undefined, CLIP: undefined, COMMON: undefined
    };
    /** @type {Record<string,{id:string,name:string}>} */
    addBookmarkInModal = {
        PAGE: undefined, TEMPLATE: undefined, CLIP: undefined, COMMON: undefined
    }

    /** @type {Array<Folder|File|ResponsePage>} */
    saveItems = [];

    searchText = "";
    folderNameChange = false;
    sideBarBookmarkSort = [
        {value: "name", label: "가나다순"},
        {value: "-name", label: "가나다역순"},
        {value: "-createdAt", label: "최신 등록순"},
        {value: "createdAt", label: "오래된 등록순"},
    ];
    sideBarOrderSort = [
        {value: "name", label: "가나다순"},
        {value: "-name", label: "가나다역순"},
        {value: "-createdAt", label: "최신 등록순"},
        {value: "createdAt", label: "오래된 등록순"},
    ];

    constructor(store) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
    }


    /**
     * 페이지 이동시에 타입별 데이터를 초기화 시켜주는 함수
     * @param {string} type 탐색기의 타입
     */
    clear(type) {
        this.folders[type] = {
            _id: "",
            name: "",
            path: "",
            depth: 1,
            files: [],
            children: [],
            createdAt: "",
            updatedAt: ""
        };
        this.bookmarks[type] = [];
        this.findFolders[type] = {
            _id: "",
            name: "",
            path: "",
            depth: 1,
            files: [],
            children: [],
            createdAt: "",
            updatedAt: ""
        };
        this.findBookmarks[type] = [];
        this.openFolders[type] = [];
        this.currentFolder[type] = undefined;
        this.folderMapById[type] = {};
        this.sortValue[type] = "createdAt";
        this.sortBookmarkValue[type] = "createdAt";
        this.editorBoardList = {name: "템플릿", link: "temp", path: "TEMPLATE"};
        this.addFolder = {TREE: false, ETC: false};
        this.addBookmark[type] = undefined;
        this.searchText = "";
        this.folderNameChange = false;
    }

    /**
     * 모달을 닫을때 그 모달에 사용된 정보를 초기화해주는 함수
     * @param {number!} depth 모달의 깊이
     * @param {string!} type 초기화할 타입
     */
    clearInModal(depth, type) {
        this.modalFolderInfo[depth].openFolders[type] = [];
        this.modalFolderInfo[depth].currentFolder[type] = undefined;
        this.addFolder = {TREE: false, ETC: false};
        this.addBookmarkInModal[type] = undefined;
    }

    refreshFolderAndBookmarks(type) {
        this.findFolders[type] = {
            _id: "",
            name: "",
            path: "",
            depth: 1,
            files: [],
            children: [],
            createdAt: "",
            updatedAt: ""
        };
        this.findBookmarks[type] = [];
        this.openFolders[type] = [];
    }

    /**
     * 부모 폴더의 고유아이디 리스트를 구하는 재귀 함수
     * @param {string!} type 폴더의 타입
     * @param {Folder} folder 자식 폴더
     * @param {string[]} parentIds 부모폴더들의 아이디 리스트
     * @return {string[]}
     */
    findParentIds(type, folder, parentIds = []) {
        folder = this.folderMapById[type][folder._id];
        if (!folder.parent) {
            return parentIds;
        }
        parentIds.push(folder.parent);
        return this.findParentIds(type, this.folderMapById[type][folder.parent], parentIds);
    }

    findParentFolders(type, folder, parentFolders = []) {
        folder = this.folderMapById[type][folder._id];
        if (parentFolders.length === 0) {
            parentFolders.push(folder);
        }
        if (!folder.parent) {
            parentFolders.pop();
            return parentFolders.reverse();
        }
        const parentFolder = this.folderMapById[type][folder.parent]
        parentFolders.push(parentFolder);
        return this.findParentFolders(type, parentFolder, parentFolders)
    }

    // findParentFolders(type, folder) {
    //     let parentFolderIds = this.findParentIds(type, folder);
    //     parentFolderIds = parentFolderIds.reverse();
    //     parentFolderIds.push(folder._id);
    //     return parentFolderIds.map(folderId => this.folderMapById[type][folderId]);
    // }
    /**
     * 검색 조건에 따른 폴더를 검색하는 함수
     * TODO 현재 사용 X
     * @param {string} type 폴더의 타입
     * @param {string?} searchType 검색 조건
     * @param {string?} folderSearchText 검색 스트링
     */
    async findFolder(type, searchType, folderSearchText) {
        this.openFolders[type] = [];
        let url = `/explorer/search?type=${type}`;
        if (searchType && searchType.length > 0) {
            url += `&searchType=${searchType}&searchString=${folderSearchText}`;
        }
        const response = await axios.get(url);
        /** @type {ResponseSuccessForm} */
        const responseData = response.data;
        this.folders[type] = responseData.data.folders;
    }

    /**
     * 폴더 트리뷰에서 검색창의 text 가 변할때마다 해당되는 폴더들을 찾아 리턴하는 함수
     * @param {string} type 탐색기의 타입
     * @param {string?} folderSearchText 검색 스트링
     */
    findFolderLive(type, folderSearchText) {
        if (folderSearchText && folderSearchText.length > 0) {
            this.openFolders[type] = []
            this.findFolders[type] = {
                _id: "",
                name: "",
                path: "",
                depth: 0,
                files: [],
                children: [],
                createdAt: "",
                updatedAt: ""
            };
            Object.values(this.folderMapById[type]).forEach((folder) => {
                if (folder.depth > 0 && folder.name.toLowerCase().indexOf(folderSearchText.toLowerCase()) > -1) {
                    const findFolder = Object.create(folder);
                    findFolder.depth = 1;
                    this.findFolders[type].children.push(findFolder);
                }
                for (const file of folder.files) {
                    if (typeof file === 'string') continue;
                    if (file.name.toLowerCase().indexOf(folderSearchText.toLowerCase()) > -1) {
                        const findFile = Object.create(file);
                        findFile.depth = 1;
                        this.findFolders[type].files.push(findFile);
                    }

                }
            });
            this.findBookmarks[type] = Object.values(this.bookmarks[type]).filter(bookmark =>
                bookmark.name.toLowerCase().indexOf(folderSearchText.toLowerCase()) > -1
            );
        } else {
            this.openFolders[type] = []
            this.findFolders[type] = {
                _id: "",
                name: "",
                path: "",
                depth: 0,
                files: [],
                children: [],
                createdAt: "",
                updatedAt: ""
            };
            this.findBookmarks[type] = [];
        }
    }

    /**
     * 폴더 리스트를 리턴하는 함수
     * @param {string} type 탐색기의 타입
     * @param {boolean?} isActionModal 모달인지의 여부
     * @return {Folder}
     */
    getFolders(type, isActionModal = false) {
        return this.searchText && this.searchText.length > 0 && !isActionModal ? this.findFolders[type] : this.folders[type];
    }

    /**
     * 즐겨찾기 리스트를 리턴하는 함수
     * @param {string} type 탐색기의 타입
     * @param {boolean?} isActionModal 모달인지의 여부
     * @return {Bookmark[]}
     */
    getBookmarks(type, isActionModal = false) {
        return this.searchText && this.searchText.length > 0 && !isActionModal ? this.findBookmarks[type] : this.bookmarks[type];
    }

    /**
     * 서버로부터 가져온 폴더리스트를 셋 하는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder} list 폴더 리스트
     * @param {number?} modalDepth 모달인 경우의 깊이
     */
    setFolderList(type, list, modalDepth) {
        if (list) {
            this.folders[type] = list;
            this.setFolderMapById(type, list);
            if (!this.currentFolder[type]) {
                this.setCurrent(type, list).then();
            }
            this.sortTree(this.folders[type], this.sortValue[type]);
            if (modalDepth !== undefined && !this.modalFolderInfo[modalDepth].currentFolder[type]) {
                this.setCurrentFolderInModal(modalDepth, type, list);
            }
        }
    }

    /**
     * 폴더의 타입과 아이디를 키로하는 맵을 만들어주는 함수
     * 부모폴더의 id도 세팅해준다.
     * @param {string} type 폴더의 타입
     * @param {Folder|Folder[]} list 폴더 정보
     * @param {string?} parent 부모 폴더의 아이디
     */
    setFolderMapById(type, list, parent = undefined) {
        if (!Array.isArray(list)) {
            this.folderMapById[type][list._id] = list;
            parent = list._id;
            list = list.children;
        }
        for (const folder of list) {
            folder.parent = parent;
            this.folderMapById[type][folder._id] = folder;
            if (folder.children && folder.children.length > 0) {
                this.setFolderMapById(type, folder.children, folder._id);
            }
        }
    }

    /**
     * 정해진 정렬값에 따라 폴더 리스트의 정렬을 변경하는 함수
     * @param {Folder|Folder[]|Bookmark[]} data 정렬할 데이터
     * @param {string} order 정렬 조건
     */
    sortTree(data, order) {
        const sortCallback = (a, b) => {
            switch (order) {
                case 'name':
                    return a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
                case '-name':
                    return b.name > a.name ? 1 : b.name < a.name ? -1 : 0;
                case 'createdAt':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case '-createdAt':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case '-expiresAt':
                    return new Date(b.expiresAt) - new Date(a.expiresAt);
                default:
                    return a.name - b.name;
            }
        }
        if (Array.isArray(data)) {
            data.sort(sortCallback);
            return;
        }
        if (data.children && data.children.length > 0) {
            data.children.sort(sortCallback);
        }
        if (data.files && data.files.length > 0) {
            data.files.sort(sortCallback);
        }
        for (const child of data.children) {
            this.sortTree(child, order);
        }
    }

    /**
     * 폴더스토어의 폴더 정렬기준을 세팅하는 함수
     * @param {string} type 탐색기 타입
     * @param {string} order 정렬 기준 스트링
     */
    changeSort(type, order) {
        this.sortValue[type] = order;
        this.sortTree(this.folders[type], order);

    }

    /**
     * 폴더스토어의 북마크 정렬 기준을 세팅하는 함수
     * @param {string} type 탐색기 타입
     * @param {string} order 정렬 기준 스트링
     */
    changeBookmarkSort(type, order) {
        this.sortBookmarkValue[type] = order;
        this.sortTree(this.bookmarks[type], order);
    }

    /**
     * 서버로부터 가져온 즐겨찾기 리스트를 세팅하는 함수
     * @param {string} type 탐색기 타입
     * @param {Bookmark[]} list 즐겨찾기 정보 리스트
     */
    setBookmarkList(type, list) {

        if (list) {
            this.bookmarks[type] = list;
            if (this.sortBookmarkValue[type]) {
                this.sortTree(this.bookmarks[type], this.sortBookmarkValue[type]);
            }
        }
    }

    /**
     * 모달 데이터 안에서의 열려 있는 폴더들의 리스트를 리턴하는 함수
     * @param {number} depth 모달의 깊이
     * @param {string} type 탐색기 타입
     * @return {string[]}
     */
    getOpenFoldersInModal(depth, type) {
        return this.modalFolderInfo[depth].openFolders[type];
    }

    /**
     * 타입별 열려 있는 폴더의 리스트릴 리턴하는 함수
     * @param {string} type 탐색기 타입
     * @param {number?} depth 모달의 깊이. 해당 변수가 있는 경우 모달로 판단. getOpenFoldersInModal 실행
     * @return {string[]}
     */
    getOpenFolders(type, depth) {
        if (depth !== undefined) {
            return this.getOpenFoldersInModal(depth, type);
        }
        return this.openFolders[type];
    }

    /**
     * 최근 유저가 클릭한 폴더를 세팅하는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder} entry 폴더 or 즐겨찾기 정보
     * @param {string?} folderName 폴더 이름 변경
     */
    async setCurrent(type, entry, folderName) {
        if (type === 'PAGE') {
            if (folderName !== 'NAMING') {
                await getPageStore().fetchNotificationPageListAndCount(this.folderMapById[type][entry._id]);
            }
        }
        if (folderName === 'NAMING') {
            this.currentFolder[type] = entry;
            this.folderNameChange = true;
        } else {
            this.setOpenAndCurrentFolder(type, this.folderMapById[type][entry._id]);
        }
    }


    /**
     * setCurrent 함수의 하위 함수로 폴더의 열림/닫힘 조작과 함께 최근 접근 폴더를 세팅하는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder} entry 폴더의 정보
     */
    setOpenAndCurrentFolder(type, entry) {
        let isParentOpen = false;
        this.findParentIds(type, entry).filter(folderId => !this.openFolders[type].includes(folderId)).forEach(folderId => {
            this.openFolders[type].push(folderId);
            isParentOpen = true;
        });
        if (this.openFolders[type].includes(entry._id)) {
            if (!isParentOpen) {
                this.openFolders[type] = this.openFolders[type].filter(id => id !== entry._id);
            }
        } else {
            this.openFolders[type].push(entry._id);
        }
        this.currentFolder[type] = this.folderMapById[type][entry._id];
    }

    /**
     * 모달 안의 폴더구조에서 유저가 최근 클릭한 폴더를 세팅해주는 함수
     * 모달 안의 폴더 트리가 변해도 모달 외부의 폴더 트리는 편하지 않아야 해서 따로 구현
     * @param {number} depth 모달의 깊이
     * @param {string} type 탐색기 타입
     * @param {Folder} entry 최근 클릭한 폴더 정보
     * @param {boolean?} withFiles 파일까지 같이 보여주는지에 대한 여부
     */
    setCurrentFolderInModal(depth, type, entry, withFiles) {
        entry = this.folderMapById[type][entry._id];
        if (!!entry.children && !!entry.files && (withFiles ? entry.children.length + entry.files.length > 0 : entry.children.length > 0)) {
            if (this.modalFolderInfo[depth].openFolders[type].includes(entry._id)) {
                this.modalFolderInfo[depth].openFolders[type] = this.modalFolderInfo[depth].openFolders[type].filter(id => id !== entry._id);
            } else {
                this.modalFolderInfo[depth].openFolders[type].push(entry._id);
            }
            this.modalFolderInfo[depth].currentFolder[type] = this.folderMapById[type][entry._id];
        } else {
            this.modalFolderInfo[depth].currentFolder[type] = this.folderMapById[type][entry._id];
        }

    }

    /**
     * 폴더를 선택하지는 않고 트리구조 내에서 열기/닫기 만을 수행하는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder} folder 폴더
     * @param {number?} depth 모달인 경우 모달의 depth
     */
    openCloseFolder(type, folder, depth = undefined) {
        if (depth !== undefined) {
            return this.openCloseFolderInModal(type, folder, depth);
        }
        if (this.openFolders[type].includes(folder._id)) {
            this.openFolders[type] = this.openFolders[type].filter(id => id !== folder._id);
        } else {
            this.openFolders[type].push(folder._id);
        }

    }

    /**
     * openCloseFolder 함수와 같은 기능을 하지만 모달인 경우에만 사용되는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder} folder 폴더 정보
     * @param {number} depth 모달의 깊이
     */
    openCloseFolderInModal(type, folder, depth) {
        if (this.modalFolderInfo[depth].openFolders[type].includes(folder._id)) {
            this.modalFolderInfo[depth].openFolders[type] = this.modalFolderInfo[depth].openFolders[type].filter(id => id !== folder._id);
        } else {
            this.modalFolderInfo[depth].openFolders[type].push(folder._id);
        }
    }

    /**
     * 최근 유저가 선택한 폴더를 리턴하는 함수
     * @param {string} type 탐색기 타입
     * @param {number?} depth 모달인 경우 모달의 깊이
     * @return {Folder}
     */
    getCurrent(type, depth) {
        if (depth !== undefined) {
            return this.getCurrentFolderInModal(depth, type);
        }
        return this.currentFolder[type];
    }

    /**
     * getCurrent 함수의 하위 함수로 모달 내에서 선택한 폴더를 리턴하는 함수
     * @param {number} depth 모달의 깊이
     * @param {string} type 탐색기 타입
     * @return {Folder}
     */
    getCurrentFolderInModal(depth, type) {
        return this.modalFolderInfo[depth].currentFolder[type];
    }

    /**
     * 서버에서 데이터를 받아오는 등의 비동기 작업을 수행 한 후에 타입별로 최근 선택폴더를 설정해주는 함수
     * 최근에 선택한 폴더가 없는 경우에만 수행
     * @param {string} type 탐색기 타입
     */
    setCurrentAfterAction(type) {
        if (this.currentFolder[type]) {
            this.currentFolder[type] = this.folderMapById[type][this.currentFolder[type]._id];
        }
    }

    /**
     * 서버에서 데이터를 받아오는 등의 비동기 작업을 수행 한 후에 모달 내의 타입별로 최근 선택폴더를 설정해주는 함수
     * 최근에 선택한 폴더가 없는 경우에만 수행
     * @param {string} type 탐색기 타입
     * @param {number} depth 모달인 경우 모달의 깊이
     */
    setCurrentAfterActionInModal(type, depth) {
        if (this.modalFolderInfo[depth].currentFolder[type]) {
            this.modalFolderInfo[depth].currentFolder[type] = this.folderMapById[type][this.modalFolderInfo[depth].currentFolder[type]._id];
        }
    }

    /**
     * 새 폴더를 추가할 때 사용되는 함수
     * @param {string} name 폴더의 이름
     * @param {string} type 탐색기 타입
     * @param {number?} depth 모달인 경우 모달의 깊이
     * @return {Promise<void>}
     */
    async addNewFolderFunc(name, type, depth) {
        const currentFolder = this.getCurrent(type, depth);
        const path = currentFolder.path.split(`${type},`)[1].slice(0, -1);
        let moreData = {
            type: type
        };
        if (name && name.length > 0) {
            moreData['name'] = name;
        }
        if (Array.isArray(path)) moreData['parentPath'] = path.join(",");
        else if (path || path.length > 0) moreData['parentPath'] = path;

        await axios.post(`/explorer/new/folder`, moreData);
    }

    /**
     * 삭제나 복제시 서버로 보낼 데이터를 가져오는 함수
     * @param {string} type 탐색기 타입
     * @return {{fileIds: string[], type: string, folderPaths: string[]}}
     */
    getModifyData(type) {
        const modifyData = {
            type: `${type}`,
            folderPaths: [],
            fileIds: [],
        };
        for (const item of this.saveItems) {
            if (item.children) {
                let path = item.path.split(`${type},`)[1].slice(0, -1);
                modifyData.folderPaths.push(path)
            } else {
                if (type === 'PAGE') {
                    modifyData.fileIds.push(item.file);
                } else {
                    modifyData.fileIds.push(item._id);
                }
            }
        }
        return modifyData;
    }

    /**
     * 체크한 아이템들을 복제하는 함수
     * @param {string} type 탐색기 타입
     * @return {Promise<void>}
     */
    async duplicateList(type) {
        const modifyData = this.getModifyData(type);
        const res = await axios.patch(`/explorer/duplicate`, modifyData);
        let status = res.data.status;
        if (status !== "success") {
            throw new Error(res.data.message);
        }

    }

    /**
     * 체크한 아이템들을 삭제하는 함수
     * @param {string} type 탐색기 타입
     * @return {Promise<void>}
     */
    async deleteList(type) {
        const modifyData = this.getModifyData(type);
        const res = await axios.delete(`/explorer/delete`, {data: modifyData});
        let status = res.data.status;
        if (status !== "success") {
            throw new Error(res.data.message);
        }
    }

    /**
     * 체크한 아이템들의 위치를 이동시키는 함수
     * @param {string} type 탐색기 타입
     * @param {number} modalDepth 모달인 경우 모달의 깊이
     * @param {boolean?} force 덮어쓰기 여부
     * @return {Promise<void>}
     */
    async moveList(type, modalDepth, force) {
        let moreData = this.getModifyData(type);
        moreData["targetPath"] = this.getCurrentFolderInModal(modalDepth, type).path.split(`${type},`)[1].slice(0, -1);
        moreData["force"] = force;

        await axios.patch(`/explorer/move`, moreData);
    }

    /**
     * 휴지통에서 선택된 아이템들을 완전삭제시키는 함수
     * @return {Promise<void>}
     */
    async completeDeleteList() {
        const moreData = {
            folderIds: [],
            fileIds: [],
        }
        this.saveItems.forEach((item) => {
            if (item.path) {
                moreData.folderIds.push(item._id)
            } else {
                moreData.fileIds.push(item._id)
            }
        })
        await axios.delete(`/explorer/completeDelete`, {data: moreData});
    }

    /**
     * 휴지통에서 선택된 아이템들을 복원시키는 함수
     * @return {Promise<void>}
     */
    async restoreList() {
        let moreData = {
            folderIds: [],
            fileIds: [],
            force: true,
        }
        this.saveItems.forEach((item) => {
            if (item.path) {
                moreData.folderIds.push(item._id)
            } else {
                moreData.fileIds.push(item._id)
            }
        })
        await axios.patch(`/explorer/restore`, moreData);
    }

    /**
     * 폴더가 존재하는지 확인하는 함수
     * @param {string} type 탐색기 타입
     * @param {string} folderId 폴더의 고유 아이디
     * @return {boolean}
     */
    checkExist(type, folderId) {
        return !!this.folderMapById[type][folderId];
    }

    /**
     * 즐겨찾기 등록/해제를 수행하는 함수
     * @param {string} type 탐색기 타입
     * @param {string} folderId 폴더의 고유 아이디
     * @param {string?} name 즐겨찾기로 지정하는 이름
     * @return {Promise<void>}
     */
    async bookmarkFolder(type, folderId, name) {
        let moreData = {type, folderId};
        if (name) {
            moreData['name'] = name;
        }
        await axios.post(`/explorer/bookmark`, moreData);
    }

    /**
     * 폴더의나 파일의 이름을 변경할 때 사용되는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder|File|ResponsePage} item 변경할 대상
     * @param {string} newName 변경할 이름
     * @return {Promise<void>}
     */
    async changeName(type, item, newName) {
        const patchData = {}
        if (type === 'PAGE') {
            if (item.file) {
                patchData['file'] = {fileId: item.file, newName: newName}
            } else {
                patchData['folderInfo'] = {folderId: item._id, newName: newName}
            }
        } else {
            if (item.board) {
                patchData['file'] = {fileId: item._id, newName: newName}
            } else {
                patchData['folderInfo'] = {folderId: item._id, newName: newName}
            }
        }
        await axios.patch(`/explorer/change/name`, patchData);
    }

    /**
     * 북마크용 폴더의나 파일의 이름을 변경할 때 사용되는 함수
     * @param {string} type 탐색기 타입
     * @param {Folder|File|ResponsePage} item 변경할 대상
     * @param {string} newName 변경할 이름
     * @return {Promise<void>}
     */
    async bookMarkChangeName(type, item, newName) {
        await axios.patch(`/explorer/bookmark`, {bookmarkId: item._id, name: newName});
    }

    /**
     * 새 폴더 입력 필드를 띄울 때 필요한 스테이트를 세팅하는 함수
     * @param {string} type 트리인지 페이지인지 구분하는 타입 값
     * @param {boolean} state 스테이트
     * 사이드 바에서 폴더 추가시 이름 새로운 아이템이 리스트에 생성될 수 있도록 하는 state 를 세팅하는 함수
     */
    setAddFolder(type, state) {
        this.addFolder[type] = state;
    }

    /**
     * 새 북마크를 만들 때 이름 설정 인풋을 띄울지 여부를 가져오는 함수
     * 모달의 경우에는 외부에는 새 북마크 인풋을 띄우지 않기 위해 제작됨.
     * @param {string} type 북마크의 타입
     * @param {boolean} isActionModal 모달인지의 여부
     * @return {undefined|{id:string,name:string}}
     */
    getAddBookmark(type, isActionModal) {
        if (isActionModal) {
            return this.addBookmarkInModal[type];
        } else {
            if (getModalStore().MODAL_INIT_OPENED.length > 0) {
                return undefined;
            }
            return this.addBookmark[type];
        }
    }

    /**
     * 사이드 바에서 즐겨찾기 추가시 이름 새로운 아이템이 리스트에 생성될 수 있도록 하는 state 를 세팅하는 함수
     * @param {string} type 탐색기 타입
     * @param {{id:string,name:string}} data 즐겨찾기 추가 필드의 가부
     * @param {boolean?} isActionModal 모달인지의 여부
     */
    setAddBookmark(type, data, isActionModal) {
        if (isActionModal) {
            this.addBookmarkInModal[type] = data;
        } else {
            this.addBookmark[type] = data;
        }
    }

    /**
     * 페이지에서 체크한 아이템들을 리턴하는 함수
     * @return {Array<Folder|File|ResponsePage>}
     */
    getSaveItems() {
        return this.saveItems;
    }

    /**
     * 체크한 아이템들을 한번에 바꾸는 함수
     * @param {Array<Folder|File|ResponsePage>} list
     */
    setSaveItems(list) {
        this.saveItems = list;
    }

    /**
     * 아이템 하나를 체크리스트에 추가하는 함수
     * @param {Folder|File|ResponsePage} item
     */
    pushSaveItems(item) {
        this.saveItems.push(item);
    }

    /**
     * 마자막 아이템 하나를 체크리스트에서 제거하는 함수
     * @return {Folder | File | ResponsePage}
     */
    popSaveItems() {
        return this.saveItems.pop();
    }

    /**
     * 기존 체크리스트에 새로운 체크리스트를 더하는 함수
     * @param {Array<Folder|File|ResponsePage>} items
     */
    concatSaveItems(items) {
        this.saveItems = this.saveItems.concat(items);
    }

    /**
     * 체크리스트에서 특정 아이템 하나를 지우는 함수
     * @param {Folder|File|ResponsePage} el
     */
    removeSaveItems(el) {
        this.saveItems = this.saveItems.filter(item => item !== el)
    }

    /**
     * 아이템이 체크리스트에 존재하는지 확인하는 함수
     * @param {Folder|File|ResponsePage} el
     * @return {boolean}
     */
    checkSaveItems(el) {
        return this.saveItems.includes(el);
    }

    /**
     * 체크리스트를 비우는 함수
     */
    clearSaveItems() {
        this.saveItems = [];
    }

    /**
     * 폴더 또는 파일 검색 텍스트를 스토어에 세팅하는 함수
     * 실제 검색 기능까지 겸한다.
     * @param {string} type 탐색기 타입
     * @param {string} text 검색 문자열
     */
    setSearchText(type, text) {
        if (text && text.length === 0) {
            text = undefined;
        }
        this.searchText = text;
        this.findFolderLive(type, text)
    }
}

export const useFolderStore = () => {
    return useStore().folderStore;
}

export const getFolderStore = () => {
    return getStore().folderStore;
}